import React, { useState } from 'react';
import './GenerateQR.css';
import QRPreview from './QRPreview';
import QRCustomization from './QRCustomization';
import QRDownload from './QRDownload';

const GenerateQR = () => {
  const [qrType, setQrType] = useState('url');
  const [qrData, setQrData] = useState('');
  const [advancedMode, setAdvancedMode] = useState(false);

  // QR Content States
  const [urlInput, setUrlInput] = useState('https://');
  const [textInput, setTextInput] = useState('');
  const [wifiData, setWifiData] = useState({
    ssid: '',
    password: '',
    security: 'WPA'
  });
  const [vcardData, setVcardData] = useState({
    name: '',
    phone: '',
    email: '',
    organization: ''
  });

  // Advanced QR Settings
  const [qrSettings, setQrSettings] = useState({
    version: 'auto',
    errorCorrectionLevel: 'M',
    margin: 4,
    scale: 10
  });

  // Visual Customization
  const [customization, setCustomization] = useState({
    fgColor: '#000000',
    bgColor: '#ffffff',
    transparentBg: false,
    gradientType: 'none',
    gradientColors: ['#000000', '#444444'],
    roundedModules: false,
    roundedFinders: false,
    preset: 'default'
  });

  // Logo
  const [logo, setLogo] = useState(null);

  // Generate QR data based on type
  React.useEffect(() => {
    let data = '';
    switch (qrType) {
      case 'url':
        data = urlInput;
        break;
      case 'text':
        data = textInput;
        break;
      case 'wifi':
        data = `WIFI:T:${wifiData.security};S:${wifiData.ssid};P:${wifiData.password};;`;
        break;
      case 'vcard':
        data = `BEGIN:VCARD
VERSION:3.0
FN:${vcardData.name}
TEL:${vcardData.phone}
EMAIL:${vcardData.email}
ORG:${vcardData.organization}
END:VCARD`;
        break;
      default:
        data = '';
    }
    setQrData(data);
  }, [qrType, urlInput, textInput, wifiData, vcardData]);

  // Apply preset
  const applyPreset = (preset) => {
    const presets = {
      default: {
        fgColor: '#000000',
        bgColor: '#ffffff',
        transparentBg: false,
        gradientType: 'none',
        roundedModules: false,
        roundedFinders: false
      },
      dark: {
        fgColor: '#ffffff',
        bgColor: '#1a1a1a',
        transparentBg: false,
        gradientType: 'none',
        roundedModules: false,
        roundedFinders: false
      },
      light: {
        fgColor: '#333333',
        bgColor: '#f5f5f5',
        transparentBg: false,
        gradientType: 'none',
        roundedModules: false,
        roundedFinders: false
      },
      blue: {
        fgColor: '#0066cc',
        bgColor: '#ffffff',
        transparentBg: false,
        gradientType: 'linear',
        gradientColors: ['#0066cc', '#00ccff'],
        roundedModules: true,
        roundedFinders: true
      }
    };

    if (presets[preset]) {
      setCustomization({ ...customization, ...presets[preset], preset });
    }
  };

  const renderInputFields = () => {
    switch (qrType) {
      case 'url':
        return (
          <div className="input-group">
            <label>Website URL</label>
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com"
            />
          </div>
        );

      case 'text':
        return (
          <div className="input-group">
            <label>Text Content</label>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter any text..."
              rows="4"
            />
          </div>
        );

      case 'wifi':
        return (
          <div className="input-group">
            <label>Network Name (SSID)</label>
            <input
              type="text"
              value={wifiData.ssid}
              onChange={(e) => setWifiData({ ...wifiData, ssid: e.target.value })}
              placeholder="MyWiFiNetwork"
            />

            <label>Password</label>
            <input
              type="text"
              value={wifiData.password}
              onChange={(e) => setWifiData({ ...wifiData, password: e.target.value })}
              placeholder="Password"
            />

            <label>Security Type</label>
            <select
              value={wifiData.security}
              onChange={(e) => setWifiData({ ...wifiData, security: e.target.value })}
            >
              <option value="WPA">WPA/WPA2</option>
              <option value="WEP">WEP</option>
              <option value="nopass">Open (No Password)</option>
            </select>
          </div>
        );

      case 'vcard':
        return (
          <div className="input-group">
            <label>Full Name</label>
            <input
              type="text"
              value={vcardData.name}
              onChange={(e) => setVcardData({ ...vcardData, name: e.target.value })}
              placeholder="John Doe"
            />

            <label>Phone Number</label>
            <input
              type="tel"
              value={vcardData.phone}
              onChange={(e) => setVcardData({ ...vcardData, phone: e.target.value })}
              placeholder="+1234567890"
            />

            <label>Email</label>
            <input
              type="email"
              value={vcardData.email}
              onChange={(e) => setVcardData({ ...vcardData, email: e.target.value })}
              placeholder="john@example.com"
            />

            <label>Organization</label>
            <input
              type="text"
              value={vcardData.organization}
              onChange={(e) => setVcardData({ ...vcardData, organization: e.target.value })}
              placeholder="Company Name"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="generate-qr">
      <div className="generate-container">
        <div className="generate-left">
          <h2>Generate QR Code</h2>

          <div className="qr-type-selector">
            <label>QR Code Type</label>
            <div className="type-buttons">
              <button
                className={qrType === 'url' ? 'active' : ''}
                onClick={() => setQrType('url')}
              >
                🌐 Website URL
              </button>
              <button
                className={qrType === 'text' ? 'active' : ''}
                onClick={() => setQrType('text')}
              >
                📝 Plain Text
              </button>
              <button
                className={qrType === 'wifi' ? 'active' : ''}
                onClick={() => setQrType('wifi')}
              >
                📶 Wi-Fi
              </button>
              <button
                className={qrType === 'vcard' ? 'active' : ''}
                onClick={() => setQrType('vcard')}
              >
                👤 Contact (vCard)
              </button>
            </div>
          </div>

          {renderInputFields()}

          <div className="mode-toggle">
            <button
              onClick={() => setAdvancedMode(!advancedMode)}
              className="toggle-btn"
            >
              {advancedMode ? '▼' : '▶'} Advanced Settings
            </button>
            {!advancedMode && (
              <p className="helper-text">
                💡 If you're not familiar with these settings, you can safely use the default options.
              </p>
            )}
          </div>

          {advancedMode && (
            <div className="advanced-settings">
              <div className="input-group">
                <label>QR Version</label>
                <select
                  value={qrSettings.version}
                  onChange={(e) => setQrSettings({ ...qrSettings, version: e.target.value })}
                >
                  <option value="auto">Auto (Recommended)</option>
                  {Array.from({ length: 40 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>Version {i + 1}</option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label>Error Correction Level</label>
                <select
                  value={qrSettings.errorCorrectionLevel}
                  onChange={(e) => setQrSettings({ ...qrSettings, errorCorrectionLevel: e.target.value })}
                >
                  <option value="L">L - Low (7%)</option>
                  <option value="M">M - Medium (15%)</option>
                  <option value="Q">Q - Quartile (25%)</option>
                  <option value="H">H - High (30%)</option>
                </select>
              </div>

              <div className="input-group">
                <label>Margin (Quiet Zone): {qrSettings.margin}</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={qrSettings.margin}
                  onChange={(e) => setQrSettings({ ...qrSettings, margin: parseInt(e.target.value) })}
                />
              </div>

              <div className="input-group">
                <label>Scale: {qrSettings.scale}</label>
                <input
                  type="range"
                  min="4"
                  max="20"
                  value={qrSettings.scale}
                  onChange={(e) => setQrSettings({ ...qrSettings, scale: parseInt(e.target.value) })}
                />
              </div>
            </div>
          )}

          <QRCustomization
            customization={customization}
            setCustomization={setCustomization}
            applyPreset={applyPreset}
            logo={logo}
            setLogo={setLogo}
          />
        </div>

        <div className="generate-right">
          <QRPreview
            data={qrData}
            settings={qrSettings}
            customization={customization}
            logo={logo}
          />

          <QRDownload
            data={qrData}
            settings={qrSettings}
            customization={customization}
            logo={logo}
            qrType={qrType}
            inputData={
              qrType === 'url' ? urlInput :
                qrType === 'text' ? textInput :
                  qrType === 'wifi' ? wifiData :
                    qrType === 'vcard' ? vcardData :
                      null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default GenerateQR;
